import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import {
  IFormattedTransaction,
  ITransaction,
  ScrollDirection,
  transactionIcon,
  transactionIconTooltip,
} from '../../models';
import { MatDialog } from '@angular/material/dialog';
import { TransactionFormatterService } from '../../services/transaction-formatter.service';
import { PaginationHelperService } from 'libs/odin-core/src/lib/services/pagination-helper.service';
import { CardHelperService } from 'libs/odin-core/src/lib/services/card-helper.service';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Router } from '@angular/router';
import { DevModeChooserService } from 'libs/odin-core/src/lib/services/dev-mode-chooser.service';
import { TransactionsListScrollService } from '../../services/transactions-list-scroll.service';

// A formatted transaction plus the per-row values the template used to compute
// by calling methods from its bindings, which re-ran for every rendered row on
// every change detection pass.
type ITransactionRow = IFormattedTransaction & {
  rowIcon: IconProp;
  rowIconTooltip: string;
  rowCard: string;
};

// How close to the bottom (in rows) the viewport must be before the next page
// is requested.
const END_OF_SCROLL_ROWS = 15;

// Enough placeholder rows to fill a tall viewport; any excess is clipped.
const SKELETON_ROW_COUNT = 14;

@Component({
  standalone: false,
  selector: 'odin-transactions-list',
  templateUrl: './transactions-list.component.html',
  styleUrls: ['./transactions-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransactionsListComponent implements OnDestroy {
  // Must match .tx-row height (71px) + the 1px mat-divider in the SCSS.
  public rowSize = 72;

  // The CDK defaults (100px / 200px) leave barely a row and a half of runway,
  // so rows were still being created as they crossed the viewport edge — a
  // blank strip at the bottom on the way down and at the top on the way up.
  // Keep six rows rendered beyond each edge, topping back up to twelve.
  public minBufferPx = this.rowSize * 6;
  public maxBufferPx = this.rowSize * 12;
  virtualScroll: CdkVirtualScrollViewport | null = null;
  private scrollSubscription: Subscription | null = null;

  // The viewport sits in the @else branch of the skeleton @if, so it isn't in
  // the DOM on first render. Wire the scroll listener up from the setter
  // rather than ngAfterViewInit, which would fire once while the viewport was
  // still absent and leave infinite scrolling permanently dead.
  @ViewChild(CdkVirtualScrollViewport)
  set virtualScrollViewport(viewport: CdkVirtualScrollViewport | undefined) {
    const next = viewport ?? null;
    if (next === this.virtualScroll) return;

    this.scrollSubscription?.unsubscribe();
    this.scrollSubscription = null;
    this.virtualScroll = next;
    if (next == null) return;

    // Restore the position saved when the list was last destroyed (e.g. the
    // user opened a transaction detail page and came back). Deferred a tick so
    // the viewport has measured its content size before scrolling.
    const savedOffset = this.scrollState.consume();
    if (savedOffset > 0) {
      setTimeout(() => next.scrollToOffset(savedOffset));
    }

    this.scrollSubscription = next.elementScrolled().subscribe(() => {
      const distFromBottom = next.measureScrollOffset('bottom');
      let inLoadZone = false;
      this.paginationHelper.detectEndOfScrollRaw(
        distFromBottom,
        this.rowSize,
        END_OF_SCROLL_ROWS,
        () => {
          inLoadZone = true;
        },
      );

      // elementScrolled() fires on every scroll frame, so without this latch
      // one pass through the load zone dispatches a page fetch per frame.
      // Each fetch re-emits the list and repaints every visible row, which is
      // what made scrolling near the bottom flash. The latch clears once the
      // user scrolls back out of the zone or the next page has been appended.
      if (!inLoadZone) {
        this.loadMoreRequested = false;
        return;
      }
      if (this.loadMoreRequested || !this.hasNext) return;

      this.loadMoreRequested = true;
      this.scrollDirection.emit(ScrollDirection.Down);
    });
  }

  @Input() set transactions(value: ITransaction[]) {
    this.formattedTransactions = this.buildRows(value);
    this.loadMoreRequested = false;
  }

  /**
   * Formatting allocates a new object per transaction, and `*cdkVirtualFor`
   * tracks rows by identity — so re-formatting the whole list on every store
   * emission used to destroy and rebuild every rendered row (reloading the
   * card-scheme images with it). Rows are cached against their source
   * transaction instead: appending a page reuses the existing row objects, and
   * a refresh or filter change produces new source objects, which invalidates
   * the cache on its own.
   */
  private buildRows(transactions: ITransaction[]): ITransactionRow[] {
    const previous = this.rowCache;
    const rowCache = new Map<ITransaction, ITransactionRow>();

    const rows = transactions
      .map((transaction) => {
        const cached = previous.get(transaction);
        if (cached) {
          rowCache.set(transaction, cached);
          return cached;
        }

        const formatted =
          this.transactionFormatterService.formatTransaction(transaction);
        const row: ITransactionRow = {
          ...formatted,
          rowIcon: this.transactionIcon(formatted),
          rowIconTooltip: transactionIconTooltip(formatted),
          rowCard: this.formattedCardNumber(formatted),
        };
        rowCache.set(transaction, row);
        return row;
      })
      .filter((row) => !row.isReversal);

    this.rowCache = rowCache;
    return rows;
  }

  public trackRow(_index: number, row: ITransactionRow): string {
    return row.crossReference;
  }

  scrollDisable = false;
  @Input() set loading(value: boolean | null) {
    this.scrollDisable = value || false;
  }

  // Shown instead of the (empty) virtual scroll viewport while the first
  // page of results is still loading, so the list doesn't flash a blank
  // panel or a spinner before any rows exist.
  public skeletonRows = Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => i);
  public get showSkeleton(): boolean {
    return this.scrollDisable && this.formattedTransactions.length === 0;
  }

  @Input() allowSelection: boolean | null = true;
  @Input() hasNext: boolean | null = false;
  @Input() hasPrev: boolean | null = false;
  @Output() scrollDirection: EventEmitter<ScrollDirection> =
    new EventEmitter<ScrollDirection>();
  @Input() refresh: EventEmitter<void> = new EventEmitter<void>();

  faAngleRight = faAngleRight;
  formattedTransactions: ITransactionRow[] = [];
  private rowCache = new Map<ITransaction, ITransactionRow>();
  private loadMoreRequested = false;

  infiniteScrollDistance = 1.4;
  infiniteScrollThrottle = 150;
  fromRoot = true;

  constructor(
    private router: Router,
    private transactionFormatterService: TransactionFormatterService,
    private paginationHelper: PaginationHelperService,
    public cardService: CardHelperService,
    private devModeChooserService: DevModeChooserService,
    private dialog: MatDialog,
    private scrollState: TransactionsListScrollService,
  ) { }

  public ngOnDestroy(): void {
    this.scrollSubscription?.unsubscribe();
    if (this.virtualScroll) {
      this.scrollState.save(this.virtualScroll.measureScrollOffset());
    }
  }

  select(transaction: ITransaction): void {
    if (!this.allowSelection) return;

    this.router.navigate([`/transactions/detail/${transaction.crossReference}`]);

    // this.dialog.open(TransactionDetailsComponent, {
    //   data: {
    //     data: new TransactionDetailModalData(transaction, [
    //       transaction.crossReference,
    //     ]),
    //     refreshTrigger: this.refresh,
    //   },
    //   panelClass: ['main-panel', 'large-modal'],
    // });
  }

  formattedCardNumber(transaction: ITransaction): string {
    const masked = transaction.maskedCard;
    if (!masked) return '-';
    const withDots = masked.replace(/\*/g, '•');
    return withDots.match(/.{1,4}/g)?.join(' ') || withDots;
  }

  transactionIcon(transaction: ITransaction): IconProp {
    return transactionIcon(transaction);
  }
}
